/**
 * Briefanrede aus dem Kundenstamm (Migration 164) — die erste Zeile des
 * Anschreibens, ohne Komma (das steht in der Vorlage: „{anrede},").
 *
 * Dieselbe Regel steht im Server: `anrede_zeile`/`anrede_fuer` in
 * hetzner/webhook/kontor_mail.py. Ändert sich eine Seite, muss die andere mit.
 */
import { parseKontakte } from "./kundeAnlage";

export const ANREDEN = ["", "frau", "herr", "familie", "paar", "firma", "du"] as const;
export type Anrede = (typeof ANREDEN)[number];

export const ANREDE_LABEL: Record<Anrede, string> = {
  "": "Unbekannt (neutral)",
  frau: "Frau",
  herr: "Herr",
  familie: "Familie",
  paar: "Frau und Herr",
  firma: "Damen und Herren",
  du: "Du (Vorname)",
};

const VORSAETZE = new Set(["familie", "fam", "frau", "herr", "eheleute"]);

function woerter(name: string): string[] {
  const w = (name || "").trim().split(/\s+/).filter(Boolean);
  while (w.length && VORSAETZE.has(w[0].toLowerCase().replace(/\.$/, ""))) w.shift();
  return w;
}

/** Die Anreden, die ein Privatkunde haben kann — „Damen und Herren" gibt es nur bei Firmen. */
export const PRIVAT_ANREDEN: readonly Anrede[] = ["", "frau", "herr", "familie", "paar", "du"];

export type AnredeQuelle = {
  anrede?: string | null;
  /** Abgelöst durch vorname/nachname (Migration 170) — nur noch Rückfall. */
  anrede_name?: string | null;
  vorname?: string | null;
  nachname?: string | null;
  name?: string | null;
  kundentyp?: string | null;
};

/** Der Name, der ohne eigene Felder genommen wird: bei „du" das erste Wort, sonst das letzte. */
export function automatischerName(anrede: string, name: string): string {
  const w = woerter(name || "");
  return ((anrede || "").trim().toLowerCase() === "du" ? w[0] : w[w.length - 1]) || "";
}

/**
 * Die Anzeigezeile `kunde.name` aus Vor- und Nachname (Migration 170).
 * „Familie" ohne Vornamen bleibt als Wort stehen, sonst läse die Kundenliste nur „Busch".
 */
export function kundenName(anrede: string, vorname: string, nachname: string): string {
  const vor = (vorname || "").trim();
  const nach = (nachname || "").trim();
  if (anrede === "familie" && !vor && nach) return `Familie ${nach}`;
  return [vor, nach].filter(Boolean).join(" ");
}

/**
 * Einen Namen aus dem Altbestand in Vor- und Nachname zerlegen — für Kunden,
 * die noch keine eigenen Felder haben. Steht `anrede_name` (Migration 164) im
 * Namen, ist das der Nachname, egal ob vorn („Ahl Karla") oder hinten.
 * „und", „+", „&" deuten auf zwei Personen: dann bleibt alles im Nachnamen.
 */
export function namenTeilen(name: string, anredeName = ""): { vorname: string; nachname: string } {
  const w = woerter(name || "");
  const rest = w.join(" ");
  const eigen = (anredeName || "").trim();
  if (eigen) {
    const klein = rest.toLowerCase();
    const e = eigen.toLowerCase();
    if (klein.endsWith(" " + e)) return { vorname: rest.slice(0, rest.length - eigen.length).trim(), nachname: eigen };
    if (klein.startsWith(e + " ")) return { vorname: rest.slice(eigen.length).trim(), nachname: eigen };
    if (klein === e) return { vorname: "", nachname: eigen };
  }
  if (w.length <= 1 || w.some((x) => /^(und|u\.|\+|&)$/i.test(x))) return { vorname: "", nachname: rest };
  return { vorname: w.slice(0, -1).join(" "), nachname: w[w.length - 1] };
}

export function anredeZeile(k: AnredeQuelle): string {
  const art = (k.anrede || "").trim().toLowerCase();
  const eigen = (k.anrede_name || "").trim();
  if (art === "du") {
    const vorname = (k.vorname || "").trim() || eigen || automatischerName(art, k.name || "");
    return vorname ? `Hallo ${vorname}` : "Hallo";
  }
  const nachname = (k.nachname || "").trim() || eigen || automatischerName(art, k.name || "");
  if (nachname) {
    if (art === "frau") return `Sehr geehrte Frau ${nachname}`;
    if (art === "herr") return `Sehr geehrter Herr ${nachname}`;
    if (art === "familie") return `Sehr geehrte Familie ${nachname}`;
    if (art === "paar") return `Sehr geehrte Frau ${nachname}, sehr geehrter Herr ${nachname}`;
  }
  if (art === "firma" || (!art && (k.kundentyp || "").trim().toLowerCase() !== "privat")) {
    return "Sehr geehrte Damen und Herren";
  }
  const voll = (k.name || "").trim().split(/\s+/).filter(Boolean).join(" ");
  // Nur ein Wort ist meist der Nachname allein — dann lieber ohne Namen.
  return voll.split(" ").length >= 2 ? `Guten Tag ${voll}` : "Guten Tag";
}

export type AnredeKunde = AnredeQuelle & { kontakte_json?: string | null };

/**
 * Die Anrede für eine Mail an `empfaenger` (die erste Adresse im An-Feld).
 *
 * Privatkunde: seine eigene Anrede. Firma: die hat keine (24.09.2026) —
 * angeschrieben wird der Ansprechpartner, an dessen Adresse die Mail geht;
 * geht sie an die Firmenadresse, „Sehr geehrte Damen und Herren".
 */
export function anredeFuer(k: AnredeKunde, empfaenger?: string): string {
  if ((k.kundentyp || "").trim().toLowerCase() === "privat") return anredeZeile(k);
  const adresse = (empfaenger || "").trim().toLowerCase();
  const person = adresse
    ? parseKontakte(k.kontakte_json || "").find(
        (p) => p.art === "person" && p.name.trim() && p.email.trim().toLowerCase() === adresse)
    : undefined;
  if (person) {
    return anredeZeile({
      anrede: person.anrede, name: person.name, vorname: person.vorname, nachname: person.nachname, kundentyp: "privat",
    });
  }
  return "Sehr geehrte Damen und Herren";
}

/**
 * Die Anrede, die wir selbst schon benutzt haben: „Guten Tag, Herr Witting"
 * → herr. Gesucht wird in den eigenen Mails, neueste zuerst; ohne passenden
 * Nachnamen zählt nichts (sonst würde der Partner aus der CC zur Anrede).
 */
export function anredeAusGruss(eigeneTexte: string[], nachname: string): Anrede {
  const nach = (nachname || "").trim();
  if (!nach) return "";
  const esc = nach.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  for (let i = eigeneTexte.length - 1; i >= 0; i--) {
    const kopf = (eigeneTexte[i] || "").split("\n").slice(0, 3).join(" ");
    if (new RegExp(`\\bFrau und Herr\\s+${esc}\\b`, "i").test(kopf)) return "paar";
    if (new RegExp(`\\bFamilie\\s+${esc}\\b`, "i").test(kopf)) return "familie";
    if (new RegExp(`\\bHerrn?\\s+${esc}\\b`, "i").test(kopf)) return "herr";
    if (new RegExp(`\\bFrau\\s+${esc}\\b`, "i").test(kopf)) return "frau";
  }
  return "";
}
